import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	CommunityPostPollAnswerViewModel,
	CommunityPostPollStatistics,
	CommunityPostPollViewModel
} from "client/Api/Communities/communityPostViewer";
import { NumberFormatter } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	Anchor,
	BusyIndicator,
	Checkbox,
	FormattedText,
	RadioButton,
	TextSkeleton,
	Toolbar,
	Typography
} from "client/Components";
import styles from "./PollViewer.module.less";
import {
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { useHubStatisticsFormatter } from "../../useHubStatisticsFormatter";
import { ProgressBar } from "client/Components/ProgressBar/ProgressBar";
import { VotersDialog } from "./VotersDialog";
import { formatUnit } from "client/Misc/cultureSpecificUtils";
import classNames from "classnames";

export type PollViewerProps = {
	postId: number;
	pollData: CommunityPostPollViewModel | null;
	isBusy: boolean;
	canViewVoters: boolean;
	onSubmitVote: (optionIds: readonly number[]) => Promise<TryResult<void>>;
	onRemoveVote: () => void;
};

export const PollViewer = (props: PollViewerProps): ReactElement | null => {
	const {
		postId,
		isBusy,
		pollData,
		canViewVoters,
		onSubmitVote,
		onRemoveVote
	} = props;


	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [isTotalVoteDialogOpen, setIsTotalVoteDialogOpen] = useState<boolean>(false);

	const numberFormatter: NumberFormatter = useHubStatisticsFormatter();

	const totalVoteCount = useMemo(
		(): number => pollData
			? getTotalVotes(pollData.statistics.voteCounts)
			: 0,
		[pollData]
	);

	const selectHandler = useCallback(
		(id: number): void => {
			if (isNil(pollData)) {
				throw new Error("Poll data is not available, but selection is made");
			}

			setSelectedIds(
				ids => {
					const newSet: Set<number> = new Set(ids);

					if (pollData.isMultiSelect) {
						if (newSet.has(id)) {
							newSet.delete(id);
						} else {
							newSet.add(id);
						}

						return newSet;
					}

					newSet.clear();
					newSet.add(id);

					return newSet;
				}
			);
		},
		[pollData]
	);

	const submitVoteHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			const optionIds: readonly number[] = Array.from(selectedIds);
			const result: TryResult<void> = await onSubmitVote(optionIds);

			if (result.wasSuccessful) {
				setSelectedIds(new Set());
			}
		},
		[
			isBusy,
			onSubmitVote,
			selectedIds
		]
	);

	const removeVoteHandler = useCallback(
		(): void => {
			if (isBusy) {
				return;
			}

			onRemoveVote();
		},
		[isBusy, onRemoveVote]
	);

	if (isBusy && isNil(pollData)) {
		return (
			<LoadingSkeleton />
		);
	}

	if (isNil(pollData)) {
		return null;
	}

	const {
		question,
		isMultiSelect,
		options,
		statistics
	} = pollData;

	const {
		didCurrentUserVote
	} = statistics;

	const renderTotalVotes = (): ReactElement => {
		const message: ReactElement = (
			<FormattedText
				text={localize("_TextWithClarificationFormat")}
				thingsToReplace={[
					{
						what: "{{Text}}",
						filler: localize("_TotalVotes")
					},
					{
						what: "{{Clarification}}",
						filler: numberFormatter(totalVoteCount)
					}
				]}
			/>
		);

		const trackingId: string = "total-votes";

		if (!canViewVoters) {
			return (
				<span
					{...setTrackingId(trackingId)}
					className={styles.totalVotes}
				>
					{message}
				</span>
			);
		}

		return (
			<Anchor
				trackingId={trackingId}
				onClick={() => setIsTotalVoteDialogOpen(true)}
				className={classNames(
					styles.totalVotes,
					styles.totalVotes_link
				)}
			>
				{message}
			</Anchor>
		);
	};

	const voteButtonText: string = localize(
		didCurrentUserVote
			? "_Voted"
			: "_Vote"
	);

	return (
		<>
			<div
				{...setTrackingId("poll-viewer")}
				className={styles.root}
			>
				{isBusy && (
					<BusyIndicator />
				)}
				<div className={styles.questionContainer}>
					<p className={styles.question}>
						{question}
					</p>
					{!didCurrentUserVote && (
						<Typography
							size="body-small"
							isItalic
							textColor="secondary"
						>
							{localize(
								isMultiSelect
									? "_SelectOneOrMultipleAnswers"
									: "_SelectOneAnswer"
							)}
						</Typography>
					)}
				</div>
				{didCurrentUserVote
					? (
						<Options
							options={options}
							statistics={statistics}
							numberFormatter={numberFormatter}
							totalVoteCount={totalVoteCount}
						/>
					)
					: (
						<OptionSelector
							options={options}
							onSelect={selectHandler}
							isMultiSelect={isMultiSelect}
							selectedIds={selectedIds}
						/>
					)
				}
				<div className={styles.footer}>
					{renderTotalVotes()}
					<div className={styles.footerActions}>
						<Toolbar>
							{didCurrentUserVote && (
								<Toolbar.Button
									kind="transparent"
									text={localize("_RemoveVote")}
									trackingId="remove-vote"
									onClick={removeVoteHandler}
									tooltip={localize("_RemoveVote")}
								/>
							)}
							<Toolbar.Button
								kind="primary"
								text={voteButtonText}
								trackingId="cast-vote"
								isDisabled={didCurrentUserVote
									|| selectedIds.size === 0
								}
								onClick={submitVoteHandler}
								tooltip={voteButtonText}
								responsiveBehavior="always-visible"
							/>
						</Toolbar>
					</div>
				</div>
			</div>
			{isTotalVoteDialogOpen && (
				<VotersDialog
					postId={postId}
					options={options}
					voteCounts={statistics.voteCounts}
					onClose={() => setIsTotalVoteDialogOpen(false)}
				/>
			)}
		</>
	);
};
PollViewer.displayName = "PollViewer";

const LoadingSkeleton = (): ReactElement => {
	return (
		<div className={styles.skeletonContainer}>
			<TextSkeleton />
			<ul className={styles.skeletonList}>
				<li className={styles.skeletonItem}>
					<TextSkeleton/>
					<TextSkeleton/>
				</li>
				<li className={styles.skeletonItem}>
					<TextSkeleton/>
					<TextSkeleton/>
				</li>
			</ul>
		</div>
	);
};
LoadingSkeleton.displayName = "LoadingSkeleton";

type OptionItem = CommunityPostPollAnswerViewModel & {
	voteCount: number;
};

type OptionsProps = {
	options: readonly CommunityPostPollAnswerViewModel[];
	statistics: CommunityPostPollStatistics;
	totalVoteCount: number;
	numberFormatter: NumberFormatter;
};

const Options = (props: OptionsProps): ReactElement => {
	const {
		options,
		statistics,
		totalVoteCount,
		numberFormatter
	} = props;

	const optionsToRender = useMemo(
		(): readonly OptionItem[] => options.map(
			x => ({
				...x,
				voteCount: statistics.voteCounts[x.id] ?? 0
			})
		),
		[options, statistics]
	);

	return (
		<OptionListLayout trackingId="option-list-results">
			{optionsToRender.map(
				(x): ReactElement => {
					const percentage: number = getPercentage(x.voteCount, totalVoteCount);
					const optionRowTrackingId: string = generateTrackingId(
						"option-item",
						`|${x.id}`
					);

					return (
						<OptionListLayout.Item
							{...setTrackingId(optionRowTrackingId)}
							key={x.id}
							id={x.id}
						>
							<div className={styles.optionStatus}>
								<div
									{...setTrackingId("percentage")}
									className={styles.optionPercentage}
								>
									{percentage}%
								</div>
								<div
									{...setTrackingId("vote-count")}
									className={styles.optionVoteCount}
								>
									{getVoteCountText(x.voteCount, numberFormatter)}
								</div>
							</div>
							<div className={styles.optionTextAndProgress}>
								<div
									{...setTrackingId("text")}
									className={styles.optionText}
								>
									{x.text}
								</div>
								<ProgressBar
									value={percentage}
									ariaLabel={localize("_PercentageBar")}
									trackingId="percentage-bar"
								/>
							</div>
						</OptionListLayout.Item>
					);
				}
			)}
		</OptionListLayout>
	);
};
Options.displayName = "Options";

function getVoteCountText(
	count: number,
	numberFormatter: NumberFormatter
): string {
	return formatUnit({
		value: count,
		singularForm: "_1Vote",
		pluralForm: "_NVotes",
		valueFormatter: numberFormatter
	});
}

type OptionSelectorProps = {
	options: readonly CommunityPostPollAnswerViewModel[];
	selectedIds: Set<number>;
	onSelect: (id: number) => void;
	isMultiSelect: boolean;
};

const OptionSelector = (props: OptionSelectorProps): ReactElement => {
	const {
		options,
		isMultiSelect,
		selectedIds,
		onSelect
	} = props;

	return (
		<OptionListLayout trackingId="option-list">
			{options.map(
				x => {
					const trackingId: string = generateTrackingId("option", `|${x.id}`);

					return (
						<OptionListLayout.Item
							key={x.id}
							id={x.id}
						>
							<div className={styles.option}>
								{isMultiSelect
									? (
										<Checkbox
											color="primary"
											onChange={() => onSelect(x.id)}
											value={selectedIds.has(x.id)}
											ellipseText={false}
											trackingId={trackingId}
											text={x.text}
										/>
									)
									: (
										<RadioButton
											color="primary"
											onChange={() => onSelect(x.id)}
											value={selectedIds.has(x.id)}
											ellipseText={false}
											trackingId={trackingId}
											text={x.text}
										/>
									)
								}
							</div>
						</OptionListLayout.Item>
					);
				}
			)}
		</OptionListLayout>
	);
};
OptionSelector.displayName = "OptionSelector";

type OptionListLayoutProps = {
	trackingId: string;
};

const OptionListLayout = (props: PropsWithChildren<OptionListLayoutProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.optionList}
		>
			{children}
		</ul>
	);
};
OptionListLayout.displayName = "OptionListLayout";

type OptionListLayoutItemProps = {
	id: number;
};

const OptionListLayoutItem = (props: PropsWithChildren<OptionListLayoutItemProps>): ReactElement => {
	const {
		id,
		children
	} = props;

	return (
		<li
			{...setTrackingId("option-item", `|${id}`)}
			className={styles.optionItem}
		>
			{children}
		</li>
	);
};
OptionListLayoutItem.displayName = "OptionListLayout.Item";

OptionListLayout.Item = OptionListLayoutItem;

function getPercentage(
	count: number,
	total: number
): number {
	if (total === 0) {
		return 0;
	}

	return Math.round((count / total) * 100);
}

export function getTotalVotes(
	voteCounts: Record<string, number>
): number {
	return Object.values(voteCounts)
		.reduce(
			(sum, current): number => sum + current,
			0
		);
}